import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GAME_SOUND_KEYS,
  GAME_SOUND_META,
  maxDurationMsFor,
  type CustomSound,
  type GameSoundKey,
} from "@spieleparadies/shared";

import { getSocket } from "../lib/socket";
import { useSoundRecorder } from "../lib/useSoundRecorder";
import { useCustomSoundsStore } from "../state/useCustomSoundsStore";

interface RoomSoundsPanelProps {
  /** Player-ID des lokalen Users (für "darfst du löschen") */
  selfId: string | null;
  isHost: boolean;
}

/**
 * Aufklappbares Panel oben in der Lobby. Erlaubt jedem Spieler, pro Sound-Slot
 * eine Sprach-Aufnahme hochzuladen. Wer zuletzt aufgenommen hat, wird neben
 * dem Slot angezeigt; Host und der Aufnehmer können den Sound zurücksetzen.
 */
export function RoomSoundsPanel({ selfId, isHost }: RoomSoundsPanelProps) {
  // Default aufgeklappt: das ist die markanteste Funktion in der Lobby und
  // soll nicht versteckt sein. Wer's nicht braucht, klappt es manuell zu.
  const [expanded, setExpanded] = useState(true);
  const [activeKey, setActiveKey] = useState<GameSoundKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sounds = useCustomSoundsStore((s) => s.sounds);
  const recorder = useSoundRecorder();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const count = useMemo(() => Object.keys(sounds).length, [sounds]);

  const playPreview = useCallback((sound: CustomSound) => {
    const el = audioRef.current;
    if (!el) return;
    el.src = sound.dataUrl;
    void el.play().catch(() => {
      // Browser-Autoplay-Policy – kein Drama, der User kann nochmal klicken
    });
  }, []);

  const handleRecord = useCallback(
    async (key: GameSoundKey) => {
      if (recorder.recording) {
        recorder.stop();
        return;
      }
      setError(null);
      setActiveKey(key);
      const result = await recorder.start({ maxDurationMs: maxDurationMsFor(key) });
      setActiveKey(null);
      if (!result) {
        if (recorder.error) setError(recorder.error);
        return;
      }
      const socket = getSocket();
      socket.emit(
        "room:sound-upload",
        {
          key,
          dataUrl: result.dataUrl,
          mime: result.mime,
          durationMs: result.durationMs,
        },
        (response) => {
          if (!response.ok) setError(response.error);
        },
      );
    },
    [recorder],
  );

  const handleReset = useCallback((key: GameSoundKey) => {
    getSocket().emit("room:sound-reset", { key });
  }, []);

  // Audio-Element aufräumen
  useEffect(() => {
    return () => {
      audioRef.current?.pause();
    };
  }, []);

  return (
    <div className={`sounds-panel hero ${expanded ? "expanded" : ""}`}>
      <button
        type="button"
        className="sounds-panel-header"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <span className="sounds-panel-title">
          <span className="sounds-panel-emoji" aria-hidden>
            🎤
          </span>
          <span className="sounds-panel-titletext">
            <span className="sounds-panel-headline">
              Eigene Sounds für diesen Raum
              {count > 0 && <span className="badge">{count}</span>}
            </span>
            <span className="sounds-panel-subline">
              Nimm dein eigenes Intro, Schießen, Sterbe-Schrei und sogar 20 s Hintergrundmusik
              auf – jeder im Raum hört's im Spiel.
            </span>
          </span>
        </span>
        <span className="sounds-panel-chevron">{expanded ? "▾" : "▸"}</span>
      </button>

      {expanded && (
        <div className="sounds-panel-body">
          {!recorder.available && (
            <p className="sounds-warn">
              Aufnahme braucht HTTPS oder localhost – auf dem Handy bitte über
              einen sicheren Tunnel öffnen.
            </p>
          )}
          {error && <p className="sounds-error">{error}</p>}

          <ul className="sounds-list">
            {GAME_SOUND_KEYS.map((key) => {
              const meta = GAME_SOUND_META[key];
              const sound = sounds[key];
              const isRecording = recorder.recording && activeKey === key;
              const canReset =
                !!sound && (isHost || sound.recordedBy.playerId === selfId);
              return (
                <li key={key} className="sound-row">
                  <div className="sound-info">
                    <div className="sound-name">
                      {meta.label}
                      {sound ? (
                        <span className="tag tag-custom">eigen</span>
                      ) : (
                        <span className="tag tag-default">Standard</span>
                      )}
                    </div>
                    <div className="sound-hint">{meta.hint}</div>
                    {sound && (
                      <div className="sound-meta">
                        Aufgenommen von <strong>{sound.recordedBy.playerName}</strong>{" "}
                        · {Math.round(sound.durationMs / 100) / 10}s ·{" "}
                        {Math.round(sound.sizeBytes / 1024)} KB
                      </div>
                    )}
                    {isRecording && (
                      <div className="sound-progress">
                        <div
                          className="sound-progress-bar"
                          style={{
                            width: `${Math.min(
                              100,
                              (recorder.elapsedMs / maxDurationMsFor(key)) * 100,
                            )}%`,
                          }}
                        />
                        <span className="sound-progress-label">
                          ● Aufnahme {(recorder.elapsedMs / 1000).toFixed(1)}s /{" "}
                          {Math.round(maxDurationMsFor(key) / 1000)}s
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="sound-actions">
                    {sound && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-small"
                        onClick={() => playPreview(sound)}
                      >
                        ▶ Anhören
                      </button>
                    )}
                    <button
                      type="button"
                      className={`btn btn-small ${isRecording ? "btn-danger" : "btn-primary"}`}
                      onClick={() => void handleRecord(key)}
                      disabled={!recorder.available || (recorder.recording && !isRecording)}
                    >
                      {isRecording ? "■ Stop" : sound ? "Neu aufnehmen" : "Aufnehmen"}
                    </button>
                    {canReset && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-small"
                        onClick={() => handleReset(key)}
                      >
                        Zurücksetzen
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          <audio ref={audioRef} preload="none" />
        </div>
      )}
    </div>
  );
}
